import { useContext, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { request } from "../../../../request";
import { MainContext } from "../../../../MainContext";
import PlaceInputs from "./PlaceInputs/PlaceInputs";
import { updateInputs } from "../_functions/updateInputs";
import { Box, CircularProgress, IconButton, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { BoundableTransactionSelector } from "./BoundableTransactionSelector/BoundableTransactionSelector";
import AddIcon from "@mui/icons-material/Add";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { icons } from "../../../../icons";
import Select, { StylesConfig } from "react-select";
import { convertToApproximateString } from "../../Inputs/convert";
import "./AddTransaction.scss";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CollapsableBox from "../../../CollapsibleBox/CollapsableBox";

interface Place {
    customerId: string | null;
    accountId: string | null;
}

interface TransactionParty {
    place: Place;
    currency_name: string;
    avg: number;
    amount: number;
}

interface LabelInput {
    customerId: string;
}

interface BoundOption {
    value: {
        data: {
            from: TransactionParty[];
            to: TransactionParty[];
            joined_transaction_id: string;
        };
    };
    label: string;
}

const default_place: TransactionParty = {
    place: {
        customerId: null,
        accountId: null,
    },
    currency_name: "",
    avg: 0,
    amount: 0,
};

const AddTransaction: React.FC<any> = ({ lastTime, getFilteredHistory }) => {
    const [from, setFrom] = useState<TransactionParty[]>([default_place]);
    const [to, setTo] = useState<TransactionParty[]>([default_place]);
    const [inputs, setInputs] = useState<LabelInput[]>([{ customerId: "" }]);
    const [comment, setComment] = useState<string>("");
    const [toBeBounded, setToBeBounded] = useState<boolean>(false);
    const [finishedBound, setFinishedBound] = useState<boolean>(false);
    const [selectedBound, setSelectedBound] = useState<BoundOption | null>(null);
    const [sendingTransaction, setSendingTransaction] = useState<boolean>(false);
    const [localTimeStamp, setLocalTimeStamp] = useState<any>();
    const [percentagesOpen, setPercentagesOpen] = useState<boolean>(true);
    const [percentages, setPercentages] = useState<any>(null);

    const [bounds, setBounds] = useState<Array<BoundOption | null>>([]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const { darkMode, updateAccounts, updateCapital, boxOpener, updateTransactions, accounts, customers, customerType, exchangeRates, texts } = useContext(MainContext);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            const newTransaction: any = {
                from,
                to,
                labels: inputs,
                toBeBounded,
                comment,
                finished: finishedBound,
            };
            if (localTimeStamp) {
                newTransaction.timeStamp = new Date(localTimeStamp.getTime() + 5 * 60 * 60 * 1000);
            }
            if (lastTime) {
                newTransaction.timeStamp = lastTime;
            }
            if (percentages) {
                newTransaction.percentages = percentages;
            }

            if (selectedBound) {
                newTransaction.joined_transaction_id = selectedBound.value.data.joined_transaction_id;

                const match = [...newTransaction.from, ...newTransaction.to].some(
                    (e: TransactionParty) => e.place.customerId?.toString() === selectedBound.value.data.from[0].place.customerId?.toString() || e.place.customerId?.toString() === selectedBound.value.data.to[0].place.customerId?.toString()
                );

                if (!match) {
                    Swal.fire({
                        title: "Wrong transaction",
                        text: "If bound is selected transaction should include the user from bound",
                        icon: "error",
                    });
                    return;
                }
                setSendingTransaction(true);
                await request.post("/transaction/", newTransaction);
                setSendingTransaction(false);
                Swal.fire({ title: "Transaction added successfully", icon: "success" });
                updateAccounts();
                updateTransactions();
                updateCapital();
                getFilteredHistory();
                boxOpener("addTransaction");
                setComment("");
                setToBeBounded(false);
                setFinishedBound(false);
                setSelectedBound(null);
                setInputs([]);
                setFrom([default_place]);
                setTo([default_place]);
            } else {
                if (newTransaction.toBeBounded) {
                    setSendingTransaction(true);
                    try {
                        await request.post("/transaction/", newTransaction);
                        Swal.fire({ title: "Transaction added successfully", icon: "success" });
                        setSendingTransaction(false);
                        boxOpener("addTransaction");
                        setComment("");
                        setToBeBounded(false);
                        setFinishedBound(false);
                        setSelectedBound(null);
                        setInputs([{ customerId: "" }]);
                        setFrom([default_place]);
                        setTo([default_place]);
                        getFilteredHistory();
                        updateAccounts();
                        updateTransactions();
                        updateCapital();
                    } catch (e) {
                        setSendingTransaction(false);

                        console.log(e);
                    }
                } else {
                    let fromAMD = 0;
                    let toAMD = 0;

                    // console.log(from.map((e) => accounts.find((a: any) => a._id.toString() === e.place.accountId?.toString()).currencies.find((c: any) => c.currency_name === e.currency_name).avg * e.amount));
                    try {
                        fromAMD = from.map((e) => accounts.find((a: any) => a._id.toString() === e.place.accountId?.toString()).currencies.find((c: any) => c.currency_name === e.currency_name).avg * e.amount).reduce((sum, a) => sum + a);
                    } catch {}
                    try {
                        //@ts-ignore
                        toAMD = to.map((e) => e.commingAvg * e.amount).reduce((sum, a) => sum + a);
                    } catch {}
                    try {
                        fromAMD = from.map((e) => accounts.find((a: any) => a._id.toString() === e.place.accountId?.toString()).currencies.find((c: any) => c.currency_name === e.currency_name).avg * e.amount).reduce((sum, a) => sum + a);
                    } catch {
                        fromAMD = toAMD;
                    }
                    try {
                        //@ts-ignore
                        toAMD = to.map((e) => e.commingAvg * e.amount).reduce((sum, a) => sum + a);
                    } catch {
                        toAMD = fromAMD;
                    }
                    const diff = toAMD - fromAMD;
                    const toAccounts = to
                        .map((e) => {
                            const account = accounts.find((a: any) => a._id.toString() === e.place.accountId?.toString());
                            if (account) {
                                return {
                                    ...account.currencies.find((c: any) => c.currency_name === e.currency_name),
                                    name: account.name,
                                };
                            } else {
                                return null;
                            }
                        })
                        .filter((e) => e);

                    const rateDiffs = toAccounts.map((a, i) => {
                        //@ts-ignore
                        const newRate = (a.value * a.avg + to[i].amount * to[i].commingAvg) / (a.value + to[i].amount);
                        const oldRate = a.value ? a.avg : newRate;
                        return {
                            name: a.name + " " + to[i].currency_name,
                            oldRate,
                            newRate,
                            diffPercentage: (newRate / oldRate - 1) * 100,
                        };
                    });
                    console.log(rateDiffs);
                    if (diff > 1 || diff < -1) {
                        Swal.fire({
                            title: diff > 1 ? "Your transaction will result in profit of " + convertToApproximateString(toAMD - fromAMD) + " AMD" : "Your transaction will result in loose of " + convertToApproximateString(toAMD - fromAMD) + " AMD",
                            icon: "question",
                            color: diff > 1 ? "green" : "red",
                            showCancelButton: true,
                            confirmButtonColor: "#3085d6",
                            cancelButtonColor: "#d33",
                            confirmButtonText: "Yes",
                            showLoaderOnConfirm: true,
                            preConfirm: async () => {
                                setSendingTransaction(true);
                                try {
                                    await request.post("/transaction/", newTransaction);
                                    setSendingTransaction(false);
                                    Swal.fire({ title: "Transaction added successfully", icon: "success" });
                                    boxOpener("addTransaction");
                                    setComment("");
                                    setToBeBounded(false);
                                    setFinishedBound(false);
                                    setSelectedBound(null);
                                    setInputs([{ customerId: "" }]);
                                    setFrom([default_place]);
                                    setTo([default_place]);
                                    updateAccounts();
                                    updateTransactions();
                                    updateCapital();
                                    getFilteredHistory();
                                } catch (e) {
                                    setSendingTransaction(false);

                                    console.log(e);
                                }
                            },
                        });
                    } else if (rateDiffs.find((r) => Math.abs(r.diffPercentage) > 0)) {
                        Swal.fire({
                            title: "Change of rate\n" + rateDiffs.map((e) => `${e.diffPercentage.toFixed(2)}% ${e.oldRate.toFixed(2)} -> ${e.newRate.toFixed(2)}`).join(" \n "),
                            // text: rateDiffs.map((e) => `${e.name} will change ${e.oldRate.toFixed(2)} -> ${e.newRate.toFixed(2)} \n`).join(" "),
                            icon: "warning",
                            color: rateDiffs.find((r) => Math.abs(r.diffPercentage) > 1) ? "red" : "green",
                            showCancelButton: true,
                            confirmButtonColor: "#3085d6",
                            cancelButtonColor: "#d33",
                            confirmButtonText: "Yes",
                            showLoaderOnConfirm: true,
                            preConfirm: async () => {
                                setSendingTransaction(true);
                                try {
                                    await request.post("/transaction/", newTransaction);
                                    setSendingTransaction(false);
                                    Swal.fire({ title: "Transaction added successfully", icon: "success" });
                                    boxOpener("addTransaction");
                                    setComment("");
                                    setToBeBounded(false);
                                    setFinishedBound(false);
                                    setSelectedBound(null);
                                    setInputs([{ customerId: "" }]);
                                    setFrom([default_place]);
                                    setTo([default_place]);
                                    updateAccounts();
                                    updateTransactions();
                                    updateCapital();
                                    getFilteredHistory();
                                } catch (e) {
                                    setSendingTransaction(false);

                                    console.log(e);
                                }
                            },
                        });
                    } else {
                        setSendingTransaction(true);
                        try {
                            await request.post("/transaction/", newTransaction);
                            Swal.fire({ title: "Transaction added successfully", icon: "success" });
                            setSendingTransaction(false);
                            boxOpener("addTransaction");
                            setComment("");
                            setToBeBounded(false);
                            setFinishedBound(false);
                            setSelectedBound(null);
                            setInputs([{ customerId: "" }]);
                            setFrom([default_place]);
                            setTo([default_place]);
                            getFilteredHistory();
                            updateAccounts();
                            updateTransactions();
                            updateCapital();
                        } catch (e) {
                            setSendingTransaction(false);

                            console.log(e);
                        }
                    }
                }
                setSendingTransaction(false);
            }
        } catch (error) {
            setSendingTransaction(false);
            console.error(error);
        }
    };

    const addInput = () => setInputs([...inputs, { customerId: "" }]);
    const removeInput = (index: number) => setInputs(inputs.filter((_, i) => i !== index));

    const addFromInput = () => setFrom([...from, { ...default_place }]);
    const removeFromInput = (index: number) => setFrom(from.filter((_, i) => i !== index));

    const addToInput = () => setTo([...to, { ...default_place }]);
    const removeToInput = (index: number) => setTo(to.filter((_, i) => i !== index));

    const handleUpdateFromInputs = (index: number, value: TransactionParty) => setFrom(updateInputs(from, index, value));

    const handleUpdateToInputs = (index: number, value: TransactionParty) => setTo(updateInputs(to, index, value));
    const transformOption = (option: any) => ({
        // isDisabled: option._id === prevInput?.place.accountId && option.currencies.length === 1,
        value: option,
        label: option?.name + (option?.type && option?.contact ? ` - ${option?.type}` : ""),
        customLabel: (
            <div className="flex items-center">
                <img
                    style={{
                        width: "30px",
                        height: "30px",
                        marginRight: "10px",
                        backgroundColor: "black",
                        borderRadius: "50%",
                        padding: "5px",
                        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
                    }}
                    //@ts-ignore
                    src={option.contact ? icons.user : icons[option?.name?.toUpperCase()] ? icons[option?.name?.toUpperCase()] : option.icon ? process.env.REACT_APP_BACK_SRC + "/api/" + "icons/" + option.icon : icons["CANCEL"]}
                />
                {option?.name + (option.type && option.contact ? ` - ${option.type}` : "")}
            </div>
        ),
        type: option.contact ? "customerId" : "accountId",
    });

    const options = [...customers]
        .filter((c) => !c.deactivated)
        .sort((a, b) => {
            if (a.name === "NO LABEL") return -1;
            if (b.name === "NO LABEL") return 1;
            return 0;
        })
        .map(transformOption);

    const selectStyles = {
        container: (base: any) => ({
            ...base,
            width: window.innerWidth <= 650 ? "350px" : "300px",
        }),
        control: (base: any, state: any) => ({
            ...base,
            borderColor: state.isDisabled ? "#333" : "gray",
            borderRadius: "0.5rem",
            padding: "0.5rem",
            boxShadow: "none",
            height: "56px",
            "&:hover": { borderColor: "blue" },
        }),
        option: (base: any, state: any) => ({
            ...base,
            backgroundColor: state.isSelected ? "#FEC901" : state.isFocused ? "lightgray" : "white",
            color: state.isSelected ? "black" : "black",
            "&:hover": {
                backgroundColor: "lightblue",
            },
        }),
        menu: (base: any) => ({
            ...base,
            marginTop: 0,
            borderRadius: "0.375rem",
        }),
        placeholder: (base: any) => ({
            ...base,
            color: "gray",
            fontSize: "0.875rem",
        }),
    };
    return (
        <div className="add-transaction-wrapper" style={{ padding: isMobile ? "" : "24px" }}>
            <form className="transaction-form" onSubmit={handleSubmit}>
                <BoundableTransactionSelector getFilteredHistory={getFilteredHistory} toBeBounded={toBeBounded} setToBeBounded={setToBeBounded} selectedBound={selectedBound} setSelectedBound={setSelectedBound} selectedCustomer={undefined} />
                {bounds.map((b, i) => {
                    return (
                        <BoundableTransactionSelector
                            toBeBounded={true}
                            setToBeBounded={false}
                            selectedBound={b}
                            setSelectedBound={(bb: BoundOption) => {
                                const newBounds = [...bounds];
                                newBounds[i] = bb;
                                setBounds(newBounds);
                            }}
                            selectedCustomer={undefined}
                            getFilteredHistory={getFilteredHistory}
                        />
                    );
                })}
                {selectedBound && (
                    <>
                        <button type="button" onClick={() => setBounds([...bounds, null])} className="add-bound-button">
                            + Add bound
                        </button>
                        {!!bounds.length && (
                            <button
                                type="button"
                                onClick={() => {
                                    request.post("/transaction/join-bounds", { bounds: [selectedBound, ...bounds.filter((b) => b)].map((b: any) => b.value.data.joined_transaction_id) }).then(() => {
                                        Swal.fire({
                                            title: "Success",
                                            icon: "success",
                                        });
                                        updateAccounts();
                                        updateTransactions();
                                        updateCapital();
                                        getFilteredHistory();
                                        setSelectedBound(null);
                                        setBounds([]);
                                    });
                                }}
                                className="join-button"
                            >
                                Join
                            </button>
                        )}
                    </>
                )}
                <hr className="section-divider" />
                <div className="input-group" style={{ border: isMobile ? "" : "1px solid #ccc" }}>
                    {from.map((input, index) => (
                        <div className="place-row" key={`from-${index}`}>
                            <div className="place-wrapper">
                                <PlaceInputs
                                    label={index === 0 ? `${texts.transactions.from}` : `${texts.transactions.from} #${index}`}
                                    values={input}
                                    prevInput={from[index - 1]}
                                    avgClose={(from[0]?.currency_name === from[1]?.currency_name && to.length === 1) || (from.length === 1 && to.length === 1) ? false : true}
                                    setValues={(newValue: TransactionParty) => handleUpdateFromInputs(index, newValue)}
                                    selectedBound={toBeBounded ? selectedBound : null}
                                    noCustomers={to[0]?.place?.customerId ? true : false}
                                    noAccounts={(from[index - 1]?.place?.accountId || from[index + 1]?.place?.accountId) && toBeBounded}
                                    lastIndex={false}
                                    // lastIndex={index === from[0] || from[index + 1]?.place.customerId}
                                    specifyRate={false}
                                    disabled={false}
                                    specificCommingAvg={false}
                                    texts={texts}
                                />
                                {!input.place.customerId ? (
                                    <div className="button-wrapper">
                                        {index === 0 ? (
                                            <Tooltip title="Click to add input">
                                                <IconButton onClick={addFromInput}>
                                                    <AddIcon className="icon-white" />
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title="Click to delete input">
                                                <IconButton onClick={() => removeFromInput(index)}>
                                                    <DeleteForeverIcon className="icon-white" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </div>
                                ) : (
                                    ""
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="input-group" style={{ border: isMobile ? "" : "1px solid #ccc" }}>
                    {to.map((input, index) => (
                        <div className="place-row" key={`to-${index}`}>
                            <div className="place-wrapper">
                                <PlaceInputs
                                    label="To"
                                    values={input}
                                    prevInput={to[index - 1]}
                                    setValues={(newValue: TransactionParty) => handleUpdateToInputs(index, newValue)}
                                    selectedBound={toBeBounded ? selectedBound : null}
                                    noCustomers={from[0]?.place.customerId ? true : false}
                                    noAccounts={(to[index - 1]?.place?.accountId || to[index + 1]?.place?.accountId) && toBeBounded}
                                    lastIndex={false}
                                    specifyRate={true}
                                    disabled={false}
                                    avgClose={false}
                                    texts={texts}
                                    specificCommingAvg={(() => {
                                        if (!from.find((e) => e.place.customerId) && !to.find((e) => e.place.customerId)) {
                                            if (from[0]?.currency_name !== to[0]?.currency_name) {
                                                if (to[0]?.currency_name === "USDT") {
                                                    //@ts-ignore
                                                    return (from[0]?.amount * from[0]?.commingAvg) / to[0]?.amount;
                                                } else if (to[0]?.currency_name === "AMD") {
                                                    return false;
                                                } else if (from[0]?.currency_name === "USDT") {
                                                    if (to[0]?.currency_name === "RUB") {
                                                        return exchangeRates["RUB"];
                                                    } else if (to[0]?.currency_name === "EUR") {
                                                        return exchangeRates["EUR"];
                                                    } else if (to[0]?.currency_name === "GEL") {
                                                        return exchangeRates["GEL"];
                                                    } else if (to[0]?.currency_name === "USD") {
                                                        return exchangeRates["USD"];
                                                    }
                                                } else {
                                                    //@ts-ignore
                                                    return (from[0]?.amount * from[0]?.commingAvg) / to[0]?.amount;
                                                }
                                            } else {
                                                let valueSum = from.map((f) => f.amount).reduce((a, b) => a + b, 0);
                                                //@ts-ignore
                                                let valueAMDSum = from.map((f) => f.amount * f.commingAvg).reduce((a, b) => a + b, 0);
                                                return valueAMDSum / valueSum;
                                            }
                                        }
                                        return false;
                                    })()}
                                />
                                {!input.place.customerId ? (
                                    <div className="button-wrapper">
                                        {index === 0 ? (
                                            <Tooltip title="Click to add input">
                                                <IconButton onClick={addToInput}>
                                                    <AddIcon className="icon-white" />
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title="Click to delete input">
                                                <IconButton onClick={() => removeToInput(index)}>
                                                    <DeleteForeverIcon className="icon-white" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </div>
                                ) : (
                                    ""
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <hr className="section-divider" />

                {inputs.map((input, index) => (
                    <div className="dynamic-input-row" key={index}>
                        <Select
                            id={index.toString()}
                            options={options}
                            isClearable
                            required
                            styles={selectStyles}
                            onChange={(e) => {
                                const updatedInputs = [...inputs];
                                updatedInputs[index] = {
                                    ...updatedInputs[index],
                                    customerId: e?.value?._id,
                                };
                                setInputs(updatedInputs);
                            }}
                            value={(() => {
                                const customerId = input?.customerId;

                                return customers.filter((e: any) => e._id === customerId).map(transformOption)[0] || null;
                            })()}
                            //@ts-ignore
                            getOptionLabel={(e) => e.customLabel}
                            filterOption={(option, input) => option.data.label.toLowerCase().includes(input.toLowerCase())}
                        />
                        <button type="button" onClick={() => removeInput(index)} className="remove-input-button" disabled={inputs.length === 1 ? true : false}>
                            X
                        </button>
                    </div>
                ))}

                <button type="button" onClick={addInput} className="add-input-button">
                    + {texts.transactions.addLabel}
                </button>

                <div className="comment-container">
                    <input id="comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={texts.transactions.writeComment} className="comment-input" />
                </div>

                {selectedBound && (
                    <label className="finished-checkbox">
                        <input type="checkbox" checked={finishedBound} onChange={() => setFinishedBound(!finishedBound)} className="checkbox-hidden" />
                        <span className={`checkbox-toggle ${finishedBound ? "checked" : ""}`}>
                            {finishedBound && (
                                <svg xmlns="http://www.w3.org/2000/svg" className="checkbox-icon" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 6.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7a1 1 0 000-1.414z" clipRule="evenodd" />
                                </svg>
                            )}
                        </span>
                        <span className="checkbox-label" style={{ color: darkMode ? "white" : "black" }}>
                            Finished
                        </span>
                    </label>
                )}
                {window.localStorage.getItem("login") === "root" && (
                    <div style={{ display: "flex", marginTop: "15px" }}>
                        <DatePicker
                            selected={localTimeStamp}
                            onChange={(date) => {
                                setLocalTimeStamp(date);
                                setTimeout(() => {
                                    //@ts-ignore
                                    document.activeElement?.blur();
                                }, 50);
                            }}
                            dateFormat="dd/MM/yyyy"
                            disabledKeyboardNavigation
                            onFocus={(e) => e.target.blur()}
                            dropdownMode="select"
                            portalId="root-portal"
                            className="p-2  border border-gray-300 rounded-lg shadow-md focus:outline-none focus:ring-blue-500 dark:bg-gray-800 dark:text-white hover:cursor-pointer"
                        />
                        <button disabled={!localTimeStamp} className="flex ml-2 px-4 py-2 bg-white rounded-md" type="button" onClick={() => setLocalTimeStamp(null)}>
                            Unselect Date
                        </button>
                    </div>
                )}
                {window.localStorage.getItem("login") === "root" && (
                    <label className="finished-checkbox">
                        <input type="checkbox" checked={percentages} onChange={() => setPercentages(percentages ? null : { hrach: 0, fahad: 0, artyom: 0 })} className="checkbox-hidden" />
                        <span className={`checkbox-toggle ${percentages ? "checked" : ""}`}>
                            {percentages && (
                                <svg xmlns="http://www.w3.org/2000/svg" className="checkbox-icon" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 6.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7a1 1 0 000-1.414z" clipRule="evenodd" />
                                </svg>
                            )}
                        </span>
                        <span className="checkbox-label" style={{ color: darkMode ? "white" : "black" }}>
                            Percentages
                        </span>
                    </label>
                )}
                {(!process.env.REACT_APP_GETCOIN && percentages) && (
                    <div style={{ padding: "10px", height: "100px", color: "white" }}>
                        <ul>
                            <li style={{ marginBottom: "10px" }}>
                                Hrach <input style={{ color: "black" }} value={percentages.hrach} onChange={(e) => setPercentages({ ...percentages, hrach: e.target.value })} type="text" />
                            </li>
                            <li style={{ marginBottom: "10px" }}>
                                Fahad <input style={{ color: "black" }} value={percentages.fahad} onChange={(e) => setPercentages({ ...percentages, fahad: e.target.value })} type="text" />
                            </li>
                            <li style={{ marginBottom: "10px" }}>
                                Artyom <input style={{ color: "black" }} value={percentages.artyom} onChange={(e) => setPercentages({ ...percentages, artyom: e.target.value })} type="text" />
                            </li>
                        </ul>
                    </div>
                )}
                <button
                    type="submit"
                    disabled={sendingTransaction}
                    className="submit-button"
                    style={{
                        backgroundColor: darkMode ? "#FEC901" : "#C1C1C1",
                        padding: "20px",
                    }}
                >
                    <span className="submit-label">{texts.transactions.addTransaction}</span>
                    {sendingTransaction && (
                        <Box display="flex" justifyContent="center" alignItems="center" style={{ color: "black" }}>
                            <CircularProgress color="inherit" size={20} />
                        </Box>
                    )}
                </button>
            </form>
        </div>
    );
};

export default AddTransaction;
